// The interviewer seat's own words (lane B7, slice S13): only the words the design system does not already hold. Every
// status, verb, rule sentence and nav item these screens draw comes from @/mc (SCORECARD_STATUS, ACKNOWLEDGMENT_STATUS,
// PANEL_CHANGE_STATUS, CANDIDATE_DECISION_STATUS, ACTION_LABELS, RECOMMENDATION_LABELS, DEBRIEF_STATUS, NAV_ITEM_LABELS,
// SLA_LABELS, SURFACE_LABELS, SCORECARD_LABELS, DEBRIEF_ROOM_LABELS, DIFF_LABELS; B6's acknowledgment dialog keeps its
// own Q-E15 words in DIALOG_LABELS). Each quote below is a verbatim line of mc-source/GLOSSARY.md
// or DS-LABEL-EXCERPTS.md; a word he has not confirmed would be pending (DS §6); S13 needs none of its own. A quote
// naming a forbidden word stays inline in its `quote` property, the one place the forbidden scan exempts.

import { defineLabels, template } from "@/mc/labels";

const KICKOFF = "\"Email includes app access, packet links, assigned ROOTS/skills, interview guidance, 24-hour scorecard SLA, confidentiality and debrief expectations.\"";
const CANDIDATE_DECISION = "| Candidate decision (HM) | \"Candidate decision Pending -> Advance/Hold/Pass.\" \"HM interviews, submits scorecard and decides advance/hold/pass.\"";
const INDEPENDENCE = "| Independent feedback / independence | \"preserve independent feedback until submission.\" \"Interviewers cannot see other scorecards before submitting.\"";

/** The screens' names and the column words of the Interviews and Debriefs lists. */
export const SCORE_LABELS = /* @__PURE__ */ defineLabels({
  interviewerPacket: { text: "Interviewer packet" },
  scorecard: { text: "Scorecard" },
  candidate: { text: "Candidate" },
  requisition: { text: "Requisition" },
  round: { text: "Round" },
  stage: { text: "Stage" },
  acknowledgment: { text: "Acknowledgment" },
  finalAlignment: { text: "Final alignment" },
});

/** DC-170: the packet's six promised contents, in the kickoff email's order (G §10); the fifth, the 24-hour scorecard
 *  SLA, is SLA_LABELS.scorecardSla. */
export const KICKOFF_CONTENT_LABELS = /* @__PURE__ */ defineLabels({
  appAccess: { text: "App access" },
  packetLinks: { text: "Packet links" },
  assignedRootsSkills: { text: "Assigned ROOTS/skills" },
  interviewGuidance: { text: "Interview guidance" },
  confidentiality: { text: "Confidentiality and debrief expectations" },
});

/** DC-171 "who sees this", DC-173 the submit verb, DC-223 the Hiring Manager's decision card. */
export const SCORECARD_PAGE_LABELS = /* @__PURE__ */ defineLabels({
  independentFeedback: { text: "Independent feedback" },
  noPeek: { text: "Interviewers cannot see other scorecards before submitting." },
  submitScorecard: { text: "Submit scorecard" },
  candidateDecision: { text: "Candidate decision" },
  hmDecidesAfterScorecard: { text: "HM interviews, submits scorecard and decides advance/hold/pass." },
});


// ---- B14: the playbook scorecard's frame (DC-244 … DC-247, his 13 Oct Corporate Interview Playbook) ------------------
// The words the shared playbook components do not draw themselves (mc PLAYBOOK_LABELS holds those): the header box, the
// stepper, the skills page's title and footer, the recommendation page's two fields, Sign and Record verification.

const PLAYBOOK_ROWS = "GLOSSARY rows, each quoting new.txt or the PNGs (his words; 13 Oct)";

/** The frame's own words (DC-247). */
export const FRAME_LABELS = /* @__PURE__ */ defineLabels({
  title: { text: "Title" },
  date: { text: "Date" },
  skills: { text: "Skills" },
  skillsPageTitle: { text: "Role-specific competency assessment" },
  skillsFooter: { text: "Competencies portal • Functional fit" },
  valuesMatrix: { text: "Values matrix" },
  justification: { text: "Justification & rationale" },
  handoff: { text: "Manager handoff & coaching remarks" },
  sign: { text: "Sign" },
  recordVerification: { text: "Record verification" },
  yourNotes: { text: "Your notes" },
});

/** The interviewer's guide card (Tejesh 26 Oct 09:02: the whole guide in one collapsed card, with one sample answer).
 *  Its title is *Interview guidance* (KICKOFF_CONTENT_LABELS: DS §3.4 renames V1's *Before you start* to it); every
 *  other word in it is the pinned guide's or a label above. The one new word marks the sample answer as an example:
 *  his "for example" (G §7, the Candidate ID row), first letter capitalised. */
export const GUIDE_LABELS = /* @__PURE__ */ defineLabels({
  example: { text: "Example" },
});

/** "Page n of N" (the stepper) and the radiogroup's name "Rating for <Value>" (the frozen accessible-name contract). */
export const FRAME_TEMPLATES = {
  pageOf: /* @__PURE__ */ template({ text: "Page {n} of {of}" }),
  ratingFor: /* @__PURE__ */ template({ text: "Rating for {name}" }),
} as const;

/** V1's own field words the rich page drew and the frame keeps unchanged until he names them (Packets.tsx before B14):
 *  the Unassessed reason, the Code of Conduct concern, the earlier scale's reservation and the field error. */
export const V1_FIELD_WORDS = {
  unassessedReason: "Why not assessed",
  otherReason: "Other reason",
  completeField: "Complete this required field.",
  concernText: "Describe the concern",
  concernAbout: "Who is the concern about?",
  reservation: "Reservation",
} as const;

/** The Unassessed reasons the server stores (V1's four, keys unchanged). */
export const UNASSESSED_REASONS = {
  not_covered_in_time: "Not covered in the time",
  candidate_could_not_speak_to_it: "Candidate could not speak to it",
  assigned_to_another_round: "Assigned to another round by agreement",
  other: "Other",
} as const;

/** Who a Code of Conduct concern is about (V1's five, keys unchanged). */
export const COC_ABOUT = {
  candidate: "The candidate",
  hiring_manager: "The Hiring Manager",
  recruiter: "The Recruiter",
  hrbp: "The HR Business Partner",
  other: "Another person",
} as const;
